import { ReactComponent as EqualLineIcon } from 'assets/icons/EqualLineIcon.svg';
import { Grid, Box, Typography, Tooltip, Switch, IconButton } from '@mui/material';
import { ConfigEntryType, ConfigType } from 'config/config';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '@mui/material/styles';
import { useConfigQuery, ConfigAPI, CONFIGS } from '@buildwise/libs/configurations';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import RenderEntryValues from './RenderEntryValues';
import useUserData from 'hooks/useUserData';

type RenderAdminConfigEntryItemProps = {
  config: ConfigType;
  entry: ConfigEntryType;
  index: number;
  isCompanyConfig: boolean;
};

function RenderAdminConfigEntryItem({
  config,
  entry,
  index,
  isCompanyConfig,
}: RenderAdminConfigEntryItemProps) {
  const theme = useTheme();
  const [openTooltip, setOpenTooltip] = useState('');
  const { companyId } = useUserData();
  const { updateConfigCache } = useConfigQuery({
    ...(isCompanyConfig && {
      companyId,
    }),
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id: entry?.id || '',
  });

  const updateConfig = async ({
    config,
    updatedConfigEntry,
  }: {
    config: ConfigType;
    updatedConfigEntry: ConfigEntryType;
  }) => {
    const updatedConfigEntries = [...config.entries];
    const index = updatedConfigEntries.findIndex((item) => item.id === updatedConfigEntry.id);

    if (index > -1) {
      updatedConfigEntries[index] = {
        ...updatedConfigEntries[index],
        ...updatedConfigEntry,
      };
    }

    try {
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        ...(isCompanyConfig && {
          companyId,
        }),
        entries: updatedConfigEntries,
      });

      setOpenTooltip(updatedConfigEntry.entryCode);
      setTimeout(() => {
        setOpenTooltip('');
      }, 2000);

      updateConfigCache({
        updatedConfig: {
          config: data?.config,
        },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const deleteConfig = async (configArray: ConfigEntryType[], index: number) => {
    try {
      configArray[index].deleteFlag = true;
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        ...(isCompanyConfig && {
          companyId,
        }),
        entries: configArray,
      });
      toast.success(`${configArray[index].entryCode} deleted successfully`);
      updateConfigCache({
        updatedConfig: {
          config: data?.config,
        },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  return (
    <Grid item md={12} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <Box
        py={0.5}
        ref={setNodeRef}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.custom.softPeach}`,
          borderRadius: '4px',
          mb: 1.5,
          p: 0.5,
          ...(isDragging && {
            boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
          }),
          '&:hover': {
            '& .sort-icon': {
              visibility: 'visible',
            },
          },
        })}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" flex={1} ml={-4}>
            <IconButton
              {...attributes}
              {...listeners}
              size="small"
              className="sort-icon"
              sx={{ visibility: 'hidden', transform: 'translateX(-30%)' }}
            >
              <EqualLineIcon width={24} color={theme.palette.custom.coolGrey} />
            </IconButton>
            <Typography
              ml={0.5}
              sx={(theme) => ({ color: theme.palette.custom.tuna, fontSize: 16, fontWeight: 700 })}
            >
              {entry?.entryCode}
            </Typography>
          </Box>
          <Box>
            <Tooltip
              open={openTooltip === entry.entryCode}
              placement="top"
              title={
                <Typography>
                  {entry?.entryCode} is {entry.isActive ? 'activated' : 'deactivated'}
                </Typography>
              }
            >
              <Switch
                disableRipple
                sx={{ m: 1 }}
                checked={entry.isActive}
                onChange={(_, checked) =>
                  updateConfig({
                    config,
                    updatedConfigEntry: {
                      ...entry,
                      isActive: checked,
                    },
                  })
                }
              />
            </Tooltip>
            <IconButton onClick={() => deleteConfig(config.entries, index)}>
              <DeleteBinLineIcon size={20} color={theme.palette.custom.redWine} />
            </IconButton>
          </Box>
        </Box>

        <RenderEntryValues entry={entry} isCompanyConfig={isCompanyConfig} />
      </Box>
    </Grid>
  );
}

export default RenderAdminConfigEntryItem;
